import React from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface PermissionGateProps {
  children: React.ReactNode;
  /** Route path that user must have access to */
  route?: string;
  /** Azure AD group IDs that user must belong to (any one of them) */
  groups?: string[];
  /** Component to show when access is denied */
  fallback?: React.ReactNode;
  /** Show default "Access Denied" message or hide completely */
  showDenied?: boolean;
}

/**
 * PermissionGate component - Conditionally renders children based on user permissions
 *
 * Usage:
 *
 * // By route:
 * <PermissionGate route="/fleet-management/dashboard">
 *   <FleetDashboard />
 * </PermissionGate>
 *
 * // By group:
 * <PermissionGate groups={['admin-group-id']}>
 *   <AdminPanel />
 * </PermissionGate>
 *
 * // With custom fallback:
 * <PermissionGate route="/admin" fallback={<div>Contact admin for access</div>}>
 *   <AdminContent />
 * </PermissionGate>
 *
 * // Hide completely when no access:
 * <PermissionGate route="/admin" showDenied={false}>
 *   <AdminButton />
 * </PermissionGate>
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  route,
  groups,
  fallback,
  showDenied = true,
}) => {
  const { hasAccess, isInGroups, loading, permissions } = usePermissions();

  // While loading permissions, show nothing or a loader
  if (loading) {
    return null;
  }

  // No permissions loaded (user not authenticated)
  if (!permissions) {
    return null;
  }

  // Check route access
  if (route) {
    const canAccess = hasAccess(route);
    if (!canAccess) {
      if (fallback) {
        return <>{fallback}</>;
      }
      if (!showDenied) {
        return null;
      }
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-heading">Access Denied</h3>
            <p className="mt-1 text-sm text-muted">
              You don't have permission to access this page.
            </p>
          </div>
        </div>
      );
    }
  }

  // Check group membership
  if (groups && groups.length > 0) {
    const isMember = isInGroups(groups);
    if (!isMember) {
      if (fallback) {
        return <>{fallback}</>;
      }
      if (!showDenied) {
        return null;
      }
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-semibold text-heading">Access Denied</h3>
            <p className="mt-1 text-sm text-muted">
              You don't have the required group membership to view this content.
            </p>
          </div>
        </div>
      );
    }
  }

  // Access granted
  return <>{children}</>;
};

export default PermissionGate;
